import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthErrorCode } from '../../../../common/enums/auth-error-code.enum';
import { CommonRoute } from '../../../../common/enums/common-route.enum';
import { CountryAvailabilityErrorCode } from '../../../../common/enums/country-availability-error-code.enum';
import { HttpCustomHeader } from '../../../../common/enums/http-custom-header.enum';
import { CLIENT_HANDLES_ERROR } from '../../../../common/http/client-handles-error.context';
import { SILENT_REQUEST } from '../../../../common/http/silent-request.context';
import { SUPPRESS_ERROR_TOAST } from '../../../../common/http/suppress-error-toast.context';
import { ToastLife } from '../../../../common/enums/toast-life.enum';
import { ToastMessage } from '../../../../common/enums/toast-message.enum';
import { ApiErrorService } from '../../../../common/services/api-error/api-error.service';
import { AuthenticationService } from '../../../../common/services/authentication/authentication.service';
import { LogFactory } from '../../../../common/services/logger/log-factory';
import { NetworkService } from '../../../../common/services/network/network.service';
import { ReturnUrlService } from '../../../../common/services/return-url/return-url.service';
import { StringService } from '../../../../common/utils/string/string.service';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AppMessage, MessageService } from '../../../core/service/message.service';

/** How long an identical generic error toast is held back after one is shown:
 *  as long as that toast stays on screen, so a burst of failures reads as one. */
const REPEAT_TOAST_WINDOW_MS = ToastLife.MEDIUM;

@Injectable({
  providedIn: 'root',
})
export class HttpErrorInterceptorService implements HttpInterceptor {
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly returnUrlService = inject(ReturnUrlService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly apiErrorService = inject(ApiErrorService);
  private readonly stringService = inject(StringService);
  private readonly network = inject(NetworkService);
  private readonly logger = inject(LogFactory).createLogger(
    HttpErrorInterceptorService,
  );
  /** When each generic error toast's text was last shown — see `showGenericError`. */
  private readonly lastShownAt = new Map<string, number>();

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const isLogoutRequest = req.url.includes(CommonRoute.LOGOUT);
        // The caller surfaces its own error message (the auth screens do this —
        // login, register, reset-password, verify-email, MFA-via-token, etc.).
        // For these we skip the generic toast, the session-expired redirect, and
        // the silent re-mint. Generalises the previous login-URL special case;
        // the URL check stays as a belt-and-braces fallback.
        const clientHandlesError =
          req.context.get(CLIENT_HANDLES_ERROR) ||
          req.url.includes(CommonRoute.LOGIN);
        // Background/sync requests handle their own failures (retry/reconcile);
        // never surface a user-facing toast for them. Still let token-refresh
        // (TOKEN_OUTDATED) run below, then rethrow quietly.
        const isSilent = req.context.get(SILENT_REQUEST);

        if (
          error.status === 403 &&
          this.apiErrorService.hasErrorCode(
            error,
            CountryAvailabilityErrorCode.APP_COUNTRY_NOT_AVAILABLE,
          )
        ) {
          if (this.router.url !== CommonRoute.COUNTRY_UNAVAILABLE) {
            void this.router.navigate([CommonRoute.COUNTRY_UNAVAILABLE], {
              queryParams: { returnUrl: this.router.url },
            });
          }
          return throwError(() => error);
        }

        // Suspended account (ADR-0149): a hard logout with a clear reason —
        // never a silent re-mint or a generic "session expired". This surfaces
        // on the refresh call after a suspend (the token-version bump yields
        // TOKEN_OUTDATED, then the refresh returns ACCOUNT_SUSPENDED) and on any
        // direct call returning it. Login/register screens handle it themselves.
        if (
          error.status === 401 &&
          this.apiErrorService.getErrorCode(error) ===
            AuthErrorCode.ACCOUNT_SUSPENDED &&
          !clientHandlesError
        ) {
          this.logger.warn('Account suspended — signing out.');
          this.messageService.add({
            severity: 'warn',
            summary: 'Account under review',
            detail:
              'Your account has been suspended. Please contact support.',
            life: ToastLife.LONG,
          });
          void this.authenticationService.cleanSession().then(() => {
            if (this.router.url !== CommonRoute.LOGIN) {
              void this.router.navigate([CommonRoute.LOGIN]);
            }
          });
          return throwError(() => error);
        }

        // Silent re-mint (ADR-0002): a TOKEN_OUTDATED 401 means the token is
        // valid but stale (permissions changed / version bumped). Refresh once
        // via the single-flight refreshSession(), then replay the ORIGINAL
        // request with the new access token. We do NOT log the user out — only
        // a failed refresh does that (handled inside refreshSession). The replay
        // uses `next.handle` directly, so a second failure cannot recurse here.
        if (
          error.status === 401 &&
          this.apiErrorService.getErrorCode(error) ===
            AuthErrorCode.TOKEN_OUTDATED &&
          !clientHandlesError
        ) {
          this.logger.debug(
            'TOKEN_OUTDATED received — refreshing session and replaying request.',
          );
          return this.authenticationService.refreshSession().pipe(
            switchMap((newAccessToken) => next.handle(this.withToken(req, newAccessToken))),
            catchError((refreshError) => {
              this.logger.warn(
                'Session refresh failed after TOKEN_OUTDATED; surfacing original error.',
                refreshError,
              );
              return throwError(() => error);
            }),
          );
        }

        // Silent (background sync) request: no toast, no login redirect — the
        // caller reconciles. TOKEN_OUTDATED refresh above already ran for it.
        if (isSilent) {
          this.logger.debug('Silent request failed', {
            status: error.status,
            method: req.method,
            url: req.url,
          });
          return throwError(() => error);
        }

        // The caller's catch logs the failure at error with its business context;
        // this line only records the request, for a developer.
        this.logger.debug('HTTP request failed', {
          status: error.status,
          method: req.method,
          url: req.url,
        });

        if (error.status === 401 && !clientHandlesError) {
          this.logger.warn('Unauthorized (401) Error Handled.');
          this.messageService.add({
            severity: 'warn',
            summary: 'Session Expired',
            detail: 'Your session has expired. Please log in again.',
            life: ToastLife.MEDIUM,
          });

          if (this.router.url !== CommonRoute.LOGIN) {
            this.router.navigate([CommonRoute.LOGIN], {
              queryParams: {
                returnUrl: this.returnUrlService.getSanitizedReturnUrl(
                  this.router.url
                ),
              },
            });
          }
        } else if (!clientHandlesError && !req.context.get(SUPPRESS_ERROR_TOAST)) {
          // Auth/validation screens surface their own specific message (e.g.
          // unverified email); don't also show the generic catch-all toast.
          // SUPPRESS_ERROR_TOAST does the same for ordinary calls whose 4xx
          // reason the caller shows itself (e.g. a refund request), while
          // keeping the 401 handling above.
          this.showGenericError(error);
        }
        // Special handling for logout failure
        if (isLogoutRequest) {
          this.logger.error('Logout Request Failed:', error);
          this.authenticationService.cleanSession().then(() => {
            this.router.navigate([CommonRoute.LOGIN]);
          });
        }

        return throwError(() => error);
      })
    );
  }

  /** Clone the request with the freshly-minted access token in the auth header. */
  private withToken(
    req: HttpRequest<unknown>,
    accessToken: string,
  ): HttpRequest<unknown> {
    const bearer =
      this.stringService.getBearerToken(accessToken) ?? accessToken;
    return req.clone({
      headers: req.headers.set(HttpCustomHeader.ACCESS_TOKEN_HEADER, bearer),
    });
  }

  /**
   * The catch-all toast, said honestly and once.
   *
   * No connection at all is not a server error: when the device reports itself
   * offline, a status-0 failure says so in its own words. When it reports itself
   * online and still gets no answer (the server is down, a captive portal), the
   * existing "Server error" text stands.
   *
   * A burst of failures — every request a screen makes, failing together —
   * shows one toast, not a stack: an identical message is held back while the
   * previous one is still on screen. A later failure, after it has gone, still
   * gets its own, so an action the user takes is never met with silence.
   */
  private showGenericError(error: HttpErrorResponse): void {
    const offline = error.status === 0 && !this.network.online();
    const message: AppMessage = offline
      ? { severity: 'warn', summary: ToastMessage.Offline, life: ToastLife.MEDIUM }
      : {
          severity: 'error',
          summary: 'Error',
          detail: this.describeError(error),
          life: ToastLife.MEDIUM,
        };
    const key = `${message.summary}|${message.detail}`;
    const now = Date.now();
    const last = this.lastShownAt.get(key);
    if (last !== undefined && now - last < REPEAT_TOAST_WINDOW_MS) return;
    this.lastShownAt.set(key, now);
    this.messageService.add(message);
  }

  /**
   * Build a user-facing message for the fallback error toast. ALWAYS generic —
   * we never surface the backend's raw `message`, which can contain internal /
   * technical detail (e.g. 'Bucket "user-assets" is not registered in BUCKETS').
   * The full error is still logged to the console for developers (see the
   * `logger.error` above). Screens that need a specific, user-actionable message
   * (login, registration, validation) show it themselves; this is only the
   * catch-all for otherwise-unhandled HTTP failures. Never exposes the status
   * code either.
   */
  private describeError(error: HttpErrorResponse): string {
    if (error.status === 0) {
      return 'Server error. Please check your connection and try again.';
    }
    if (error.status >= 500) {
      return 'Server error. Please try again later.';
    }
    return 'Something went wrong. Please try again.';
  }
}

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const interceptor = inject(HttpErrorInterceptorService);
  const nextHandler: HttpHandler = {
    handle: (request: HttpRequest<any>) => next(request),
  };
  return interceptor.intercept(req, nextHandler);
};
